import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameId } from './games';
import {
  finish as finishRound,
  getResult,
  getStatus,
  loadState,
  saveState,
  type DailyResult,
  type DailyStatus,
} from './dailySession';

/**
 * Wraps the daily-session store for a single game.
 *
 * Read `status` before rendering a board: when it isn't 'playing' the round is
 * over and the game should render its result screen instead. `restored` is the
 * in-progress snapshot to seed state from, or null for a fresh round.
 */
export function useDailySession<S>(gameId: GameId, dateKey: string) {
  // Read once per (game, date). Reading during render keeps the very first
  // paint correct — a finished round must never flash a playable board.
  const [initial] = useState(() => ({
    status: getStatus(gameId, dateKey),
    state: loadState<S>(gameId, dateKey),
    result: getResult(gameId, dateKey),
  }));

  const [status, setStatus] = useState<DailyStatus>(initial.status);
  const [result, setResult] = useState<DailyResult | null>(initial.result);

  // Guards double-resolution: a game may detect a win from more than one place
  // (an effect and an event handler) within the same tick.
  const settled = useRef(initial.status !== 'playing');

  const persist = useCallback(
    (state: S) => {
      if (settled.current) return;
      saveState<S>(gameId, dateKey, state);
    },
    [gameId, dateKey],
  );

  const finish = useCallback(
    (outcome: 'won' | 'lost', summary: Omit<DailyResult, 'finishedAt'> = {}) => {
      if (settled.current) return;
      settled.current = true;
      finishRound(gameId, dateKey, outcome, summary);
      setResult({ ...summary, finishedAt: Date.now() });
      setStatus(outcome);
    },
    [gameId, dateKey],
  );

  return {
    /** 'playing' | 'won' | 'lost' — anything but 'playing' means locked. */
    status,
    isFinished: status !== 'playing',
    /** Snapshot to seed state from on re-entry; null for a fresh round. */
    restored: initial.state,
    result,
    /** Snapshot mid-round progress so backing out doesn't lose it. */
    persist,
    /** Resolve the round; further calls are ignored. */
    finish,
  };
}

/**
 * The in-progress half of the session, for games that only need to survive
 * back-navigation (the loader already handles the finished case).
 *
 * `restored` is read once on mount so the very first render can seed state
 * directly from it rather than flashing an empty board.
 */
export function useDailySnapshot<S>(gameId: GameId, dateKey: string) {
  const [restored] = useState(() => loadState<S>(gameId, dateKey));
  const save = useCallback(
    (state: S) => saveState<S>(gameId, dateKey, state),
    [gameId, dateKey],
  );
  return { restored, save };
}

/**
 * Persists a snapshot whenever `state` changes, and once more on unmount.
 *
 * The unmount write matters most: React Router unmounts the game the instant
 * the player taps back, so without it the last move before leaving is lost.
 */
export function usePersistedState<S>(
  persist: (state: S) => void,
  state: S,
  enabled: boolean,
): void {
  const latest = useRef(state);
  latest.current = state;
  const persistRef = useRef(persist);
  persistRef.current = persist;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    if (!enabled) return;
    persist(state);
  }, [persist, state, enabled]);

  useEffect(
    () => () => {
      if (enabledRef.current) persistRef.current(latest.current);
    },
    [],
  );
}
